import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, debounceTime, forkJoin } from 'rxjs';
import { FluidModule } from 'primeng/fluid';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { LayoutService } from '@/layout/service/layout.service';
import { DashboardService, DashboardOverview, WorkflowStat, FunnelStep, AiMetrics } from '@/pages/service/core/dashboard.service';
import { MetricWidgetComponent } from './components/metric-widget.component';

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule, MetricWidgetComponent, ChartModule, FluidModule, TableModule, SelectModule, TagModule, SkeletonModule],
    template: `
        <section class="dashboard">
            <header class="dashboard-header">
                <div>
                    <div class="dashboard-title">AI Engine Analytics</div>
                    <div class="dashboard-subtitle">Workflow health and activity snapshot</div>
                </div>
                <div class="flex items-center gap-2">
                    <span class="text-sm text-color-secondary">Period:</span>
                    <p-select appendTo="body" [filter]="true" filterPlaceholder="Search" [options]="dayOptions" [(ngModel)]="selectedDays" optionLabel="label" optionValue="value" (onChange)="onDaysChange()" styleClass="w-28"></p-select>
                </div>
            </header>

            <!-- ── SECTION 1: OVERVIEW ── -->
            <div class="dashboard-section-label">Overview</div>
            <div class="dashboard-metrics">
                <div class="metric-slot">
                    <app-metric-widget
                        title="Active Conversations"
                        [value]="overview?.activeConversations ?? '—'"
                        subtitle="Currently active users"
                        status="success"
                        icon="pi-comments">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Completed Today"
                        [value]="overview?.completedToday ?? '—'"
                        [subtitle]="'Last ' + selectedDays + ' days'"
                        status="info"
                        icon="pi-check-circle">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Handoffs"
                        [value]="overview?.handoff ?? '—'"
                        subtitle="Escalations"
                        status="warning"
                        icon="pi-arrow-right-arrow-left">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Avg Response Time"
                        [value]="avgResponseLabel"
                        subtitle="Bot latency"
                        status="info"
                        icon="pi-clock">
                    </app-metric-widget>
                </div>
            </div>

            <!-- ── SECTION 4: AI METRICS ── -->
            <div class="dashboard-section-label">AI Metrics</div>
            <div class="dashboard-metrics">
                <div class="metric-slot">
                    <app-metric-widget
                        title="Intents Detected"
                        [value]="aiMetrics?.intentDetected ?? '—'"
                        subtitle="Total intents identified"
                        status="success"
                        icon="pi-bolt">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Intent Fallback"
                        [value]="aiMetrics?.intentFallback ?? '—'"
                        subtitle="AI failed to detect"
                        status="danger"
                        icon="pi-exclamation-triangle">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Validation Failures"
                        [value]="aiMetrics?.validationFailures ?? '—'"
                        subtitle="User input errors"
                        status="warning"
                        icon="pi-times-circle">
                    </app-metric-widget>
                </div>
                <div class="metric-slot">
                    <app-metric-widget
                        title="Fallback Rate"
                        [value]="aiMetrics ? (aiMetrics.fallbackRate | number:'1.1-1') + '%' : '—'"
                        subtitle="% of unhandled intents"
                        [status]="fallbackRateStatus"
                        icon="pi-percentage">
                    </app-metric-widget>
                </div>
            </div>

            <p-fluid class="dashboard-grid">
                <!-- ── SECTION 2: WORKFLOW STATS ── -->
                <div class="grid-card grid-card--full">
                    <div class="card">
                        <div class="font-semibold text-xl mb-4">Workflow Stats</div>
                        <p-table [value]="workflowStats" [loading]="loadingWorkflowStats" stripedRows>
                            <ng-template #header>
                                <tr>
                                    <th>Workflow ID</th>
                                    <th pSortableColumn="totalConversations">Conversations <p-sortIcon field="totalConversations"></p-sortIcon></th>
                                    <th pSortableColumn="stepsCompleted">Steps Completed <p-sortIcon field="stepsCompleted"></p-sortIcon></th>
                                    <th pSortableColumn="handoff">Handoff <p-sortIcon field="handoff"></p-sortIcon></th>
                                    <th pSortableColumn="failures">Failures <p-sortIcon field="failures"></p-sortIcon></th>
                                    <th pSortableColumn="completionRate">Completion % <p-sortIcon field="completionRate"></p-sortIcon></th>
                                    <th pSortableColumn="handoffRate">Handoff % <p-sortIcon field="handoffRate"></p-sortIcon></th>
                                    <th pSortableColumn="failureRate">Failure % <p-sortIcon field="failureRate"></p-sortIcon></th>
                                    <th>Health</th>
                                </tr>
                            </ng-template>
                            <ng-template #body let-row>
                                <tr>
                                    <td class="font-mono text-xs text-color-secondary">{{ row.workflowId | slice:0:8 }}…</td>
                                    <td>{{ row.totalConversations }}</td>
                                    <td>{{ row.stepsCompleted }}</td>
                                    <td>{{ row.handoff }}</td>
                                    <td>
                                        <span [class]="row.failures > 0 ? 'text-red-500 font-semibold' : 'text-green-500'">{{ row.failures }}</span>
                                    </td>
                                    <td>
                                        <span [class]="getRateClass(calcCompletionRate(row), true)">
                                            {{ calcCompletionRate(row) | number:'1.1-1' }}%
                                        </span>
                                    </td>
                                    <td>
                                        <span [class]="getRateClass(calcHandoffRate(row), false)">
                                            {{ calcHandoffRate(row) | number:'1.1-1' }}%
                                        </span>
                                    </td>
                                    <td>
                                        <span [class]="getRateClass(calcFailureRate(row), false)">
                                            {{ calcFailureRate(row) | number:'1.1-1' }}%
                                        </span>
                                    </td>
                                    <td>
                                        <p-tag [value]="getHealthLabel(row)" [severity]="getHealthSeverity(row)"></p-tag>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template #emptymessage>
                                <tr><td colspan="9" class="text-center text-color-secondary py-4">No workflow data available</td></tr>
                            </ng-template>
                        </p-table>
                    </div>
                </div>

                <!-- ── SECTION 3: FUNNEL ── -->
                <div class="grid-card grid-card--full">
                    <div class="card">
                        <div class="flex justify-between items-center mb-4">
                            <div class="font-semibold text-xl">Conversation Funnel</div>
                            <p-select appendTo="body" [filter]="true" filterPlaceholder="Search"
                                [options]="workflowOptions"
                                [(ngModel)]="selectedWorkflowId"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select a workflow"
                                (onChange)="onWorkflowSelect()"
                                styleClass="w-80"
                                [disabled]="workflowStats.length === 0">
                            </p-select>
                        </div>
                        <div *ngIf="funnelSteps.length > 0; else noFunnel">
                            <p-chart type="bar" [data]="funnelChartData" [options]="funnelChartOptions" height="320px"></p-chart>
                            <div class="funnel-drop-table mt-4">
                                <table class="w-full text-sm gh-table">
                                    <thead>
                                        <tr class="text-color-secondary border-b border-surface">
                                            <th class="text-left py-2">Step</th>
                                            <th class="text-right py-2">Entered</th>
                                            <th class="text-right py-2">Completed</th>
                                            <th class="text-right py-2">Drop-off</th>
                                            <th class="text-right py-2">Drop Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let step of funnelSteps; let i = index" class="border-b border-surface">
                                            <td class="py-2 font-medium">{{ step.stepKey }}</td>
                                            <td class="text-right py-2">{{ step.entered }}</td>
                                            <td class="text-right py-2">{{ step.completed }}</td>
                                            <td class="text-right py-2" [class.text-red-500]="step.entered - step.completed > 0">
                                                {{ step.entered - step.completed > 0 ? '-' + (step.entered - step.completed) : '—' }}
                                            </td>
                                            <td class="text-right py-2">
                                                <span [class]="getDropRateClass(i)">{{ getDropRate(i) }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <ng-template #noFunnel>
                            <div class="text-center text-color-secondary py-8">
                                <i class="pi pi-filter text-4xl mb-3 block opacity-30"></i>
                                <p>Select a workflow to view its funnel</p>
                            </div>
                        </ng-template>
                    </div>
                </div>

          
            </p-fluid>
        </section>
    `
})
export class DashboardComponent implements OnInit, OnDestroy {
    // Days selector
    selectedDays = 7;
    dayOptions = [
        { label: 'Last 7 days', value: 7 },
        { label: 'Last 14 days', value: 14 },
        { label: 'Last 30 days', value: 30 }
    ];

    // Overview
    overview: DashboardOverview | null = null;

    // Workflow stats
    workflowStats: WorkflowStat[] = [];
    loadingWorkflowStats = false;

    // Funnel
    funnelSteps: FunnelStep[] = [];
    funnelChartData: any;
    funnelChartOptions: any;
    selectedWorkflowId: string | null = null;

    // AI Metrics
    aiMetrics: AiMetrics | null = null;

    private subscriptions = new Subscription();

    constructor(
        private layoutService: LayoutService,
        private dashboardService: DashboardService
    ) {
        this.subscriptions.add(
            this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
                if (this.funnelSteps.length > 0) {
                    this.buildFunnelChart();
                }
            })
        );
    }

    ngOnInit() {
        this.fetchDashboardData();
    }

    // ── COMPUTED: overview ──────────────────────────────────────────────────

    get avgResponseLabel(): string {
        if (!this.overview) return '—';
        const ms = this.overview.avgResponseTime;
        return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
    }

    get fallbackRateStatus(): 'success' | 'warning' | 'danger' | 'info' {
        if (!this.aiMetrics) return 'info';
        if (this.aiMetrics.fallbackRate < 10) return 'success';
        if (this.aiMetrics.fallbackRate < 25) return 'warning';
        return 'danger';
    }

    // ── COMPUTED: workflow options for funnel selector ─────────────────────

    get workflowOptions(): { label: string; value: string }[] {
        return this.workflowStats
            .filter((ws) => !!ws.workflowId)
            .map((ws) => ({
                label: `${ws.workflowId.slice(0, 8)}… (${ws.totalConversations} conv.)`,
                value: ws.workflowId
            }));
    }

    // ── DATA FETCHING ──────────────────────────────────────────────────────

    private fetchDashboardData(): void {
        const days = this.selectedDays;
        this.loadingWorkflowStats = true;

        this.subscriptions.add(
            forkJoin({
                overview: this.dashboardService.getOverview(days),
                workflowStats: this.dashboardService.getWorkflowStats(days),
                aiMetrics: this.dashboardService.getAiMetrics(days)
            }).subscribe({
                next: ({ overview, workflowStats, aiMetrics }) => {
                    this.overview = overview;
                    this.workflowStats = Array.isArray(workflowStats) ? workflowStats : [];
                    this.aiMetrics = aiMetrics;
                    this.loadingWorkflowStats = false;
                    if (this.workflowStats.length > 0 && !this.selectedWorkflowId) {
                        this.selectedWorkflowId = this.workflowStats[0].workflowId;
                        this.onWorkflowSelect();
                    }
                },
                error: () => {
                    this.loadingWorkflowStats = false;
                }
            })
        );
    }

    onDaysChange(): void {
        this.overview = null;
        this.aiMetrics = null;
        this.funnelSteps = [];
        this.selectedWorkflowId = null;
        this.fetchDashboardData();
    }

    onWorkflowSelect(): void {
        if (!this.selectedWorkflowId) return;
        this.subscriptions.add(
            this.dashboardService.getFunnel(this.selectedWorkflowId).subscribe({
                next: (steps) => {
                    this.funnelSteps = Array.isArray(steps) ? steps : [];
                    this.buildFunnelChart();
                },
                error: () => {
                    this.funnelSteps = [];
                }
            })
        );
    }

    // ── FUNNEL CHART ───────────────────────────────────────────────────────

    private buildFunnelChart(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

        const labels = this.funnelSteps.map((s) => s.stepKey);

        // Color bars: red if drop-off > 30% of entered, orange > 15%, else primary
        const backgroundColors = this.funnelSteps.map((s) => {
            const dropRate = s.entered > 0 ? (s.entered - s.completed) / s.entered : 0;
            if (dropRate > 0.3) return documentStyle.getPropertyValue('--p-red-400');
            if (dropRate > 0.15) return documentStyle.getPropertyValue('--p-orange-400');
            return documentStyle.getPropertyValue('--p-primary-400');
        });

        this.funnelChartData = {
            labels,
            datasets: [
                {
                    label: 'Entered',
                    data: this.funnelSteps.map((s) => s.entered),
                    backgroundColor: documentStyle.getPropertyValue('--p-primary-200'),
                    borderColor: documentStyle.getPropertyValue('--p-primary-400'),
                    borderWidth: 1
                },
                {
                    label: 'Completed',
                    data: this.funnelSteps.map((s) => s.completed),
                    backgroundColor: backgroundColors,
                    borderColor: backgroundColors,
                    borderWidth: 1
                }
            ]
        };

        this.funnelChartOptions = {
            maintainAspectRatio: false,
            plugins: {
                legend: { labels: { color: documentStyle.getPropertyValue('--text-color') } },
                tooltip: {
                    callbacks: {
                        afterLabel: (ctx: any) => {
                            const step = this.funnelSteps[ctx.dataIndex];
                            if (!step) return '';
                            const drop = step.entered - step.completed;
                            const rate = step.entered > 0 ? ((drop / step.entered) * 100).toFixed(1) : '0.0';
                            return `Drop-off: ${drop} (${rate}%)`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: textColorSecondary },
                    grid: { color: surfaceBorder, drawBorder: false }
                },
                y: {
                    beginAtZero: true,
                    ticks: { color: textColorSecondary, precision: 0 },
                    grid: { color: surfaceBorder, drawBorder: false }
                }
            }
        };
    }

    // ── FUNNEL HELPERS ─────────────────────────────────────────────────────

    getDropRate(index: number): string {
        const step = this.funnelSteps[index];
        if (!step || !step.entered) return '—';
        const drop = step.entered - step.completed;
        return `${((drop / step.entered) * 100).toFixed(1)}%`;
    }

    getDropRateClass(index: number): string {
        const step = this.funnelSteps[index];
        if (!step || !step.entered) return '';
        const rate = (step.entered - step.completed) / step.entered;
        if (rate > 0.3) return 'text-red-500 font-semibold';
        if (rate > 0.15) return 'text-orange-500';
        return 'text-green-500';
    }

    // ── WORKFLOW STATS HELPERS ─────────────────────────────────────────────

    calcCompletionRate(row: WorkflowStat): number {
        if (row.completionRate != null) return row.completionRate;
        return row.totalConversations > 0 ? (row.stepsCompleted / row.totalConversations) * 100 : 0;
    }

    calcHandoffRate(row: WorkflowStat): number {
        if (row.handoffRate != null) return row.handoffRate;
        return row.totalConversations > 0 ? (row.handoff / row.totalConversations) * 100 : 0;
    }

    calcFailureRate(row: WorkflowStat): number {
        if (row.failureRate != null) return row.failureRate;
        return row.totalConversations > 0 ? (row.failures / row.totalConversations) * 100 : 0;
    }

    getHealthLabel(row: WorkflowStat): string {
        const fr = this.calcFailureRate(row);
        if (fr < 10) return 'Healthy';
        if (fr < 25) return 'Degraded';
        return 'Critical';
    }

    getHealthSeverity(row: WorkflowStat): 'success' | 'warn' | 'danger' {
        const fr = this.calcFailureRate(row);
        if (fr < 10) return 'success';
        if (fr < 25) return 'warn';
        return 'danger';
    }

    getRateClass(rate: number | null, higherIsBetter: boolean): string {
        if (rate === null) return '';
        if (higherIsBetter) {
            if (rate >= 70) return 'text-green-500 font-semibold';
            if (rate >= 40) return 'text-orange-500';
            return 'text-red-500';
        } else {
            if (rate < 10) return 'text-green-500';
            if (rate < 25) return 'text-orange-500';
            return 'text-red-500 font-semibold';
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
